import useSWR from "swr";
import { useState } from "react";
import { useRouter } from "next/router";
import {
  get,
  round,
  parseInt,
  toNumber,
  groupBy,
  map,
  isEmpty,
  filter,
} from "lodash";
import { productPage } from "grandus-lib/utils/fetches";
import EnhancedEcommerce from "utils/ecommerce";
import TagManager from "grandus-lib/utils/gtag";
import FBPixel from "grandus-lib/utils/fbpixel";
import { reqGetHeadersFront } from "grandus-lib/utils";

import {
  ALTERNATIVE_PRODUCTS_CROSSSALE_HASH,
  COUNTDOWN_PARAM_HASH,
} from "constants/ProductConstants";

import Gallery from "components/gallery/Gallery";
import PriceBox from "components/product/detail/priceBox/PriceBox";
import {LeadhubViewContent} from 'utils/leadhub';
// import RegisterUserPrice from "components/product/detail/registerUserPrice/RegisterUserPrice";

import styles from "./product.page.module.scss";

import dynamic from "next/dynamic";
import useWishlist from "grandus-lib/hooks/useWishlist";
import useCompare from "grandus-lib/hooks/useCompare";
const MetaData = dynamic(() =>
  import("grandus-lib/components-atomic/MetaData")
);
const Breadcrumbs = dynamic(() => import("components/breadcrumbs/Breadcrumbs"));
const Image = dynamic(() =>
  import("grandus-lib/components-atomic/image/Image")
);

import AddToCartButton from "components/cart/AddToCartButton";

const CrossSale = dynamic(() => import("components/product/detail/crossSale"), {
  ssr: false,
});

import useWebInstance from "grandus-lib/hooks/useWebInstance";
import useUser from "grandus-lib/hooks/useUser";
import ProductsCarousel from "components/productsCarousel";
import FBCAPI from "grandus-lib/utils/fbcapi";
import Remarketing from "grandus-lib/utils/remarketing";

const QuatroButton = ({ price }) => {
  const { settings } = useWebInstance();
  if (
    !(
      price &&
      parseInt(get(settings, "quatro_active")) &&
      get(settings, "quatro_id")
    )
  ) {
    return "";
  }

  if (
    !(
      price > toNumber(get(settings, "quatro_available_from_price", 0)) &&
      price < toNumber(get(settings, "quatro_available_to_price", Infinity))
    )
  ) {
    return "";
  }
  return (
    <a
      rel="nofollow"
      target="_blank"
      href={`https://quatro.vub.sk/kalkulacka/${get(
        settings,
        "quatro_id"
      )}?cenaTovaru=${price}`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24.309"
        height="21.733"
        viewBox="0 0 24.309 21.733"
      >
        <g transform="translate(-495.637 -260.732)">
          <path
            d="M506.505,264.976h-1.023a9.845,9.845,0,1,0,6.961,16.806l.724-.723-6.662-6.661Zm3.722,16.037a7.8,7.8,0,1,1-5.768-13.924v8.156Z"
            transform="translate(0 -2.202)"
          />
          <path
            d="M522.966,260.732h-1.023v11.65h11.65v-1.023A10.639,10.639,0,0,0,522.966,260.732Zm1.023,9.6v-7.5a8.6,8.6,0,0,1,7.5,7.5Z"
            transform="translate(-13.647)"
          />
        </g>
      </svg>
      Quatro kalkulačka splátok
    </a>
  );
};

const DeliveryInfo = ({ options = {} }) => {
  return (
    <div
      className={[styles.deliveryInfo, styles[get(options, "class", "")]].join(
        " "
      )}
    >
      <p>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="23.772"
          height="15.458"
          viewBox="0 0 23.772 15.458"
        >
          <path
            d="M743.347,23.864h-12.68V24.95h11.594v8.273H730.667v1.086h12.68Z"
            transform="translate(-727.102 -23.864)"
          />
          <path
            d="M788.824,30.985l-2.089-3.663h-3.922v9.626H789.7V31.486ZM786.1,28.408l1.309,2.3H783.9v-2.3Zm2.511,7.454H783.9V31.789h4.143l.243.139.33.188Z"
            transform="translate(-765.929 -26.439)"
          />
          <path
            d="M735.116,67.849a2.112,2.112,0,1,0,2.112,2.112A2.112,2.112,0,0,0,735.116,67.849Zm0,3.138a1.026,1.026,0,1,1,1.026-1.026A1.027,1.027,0,0,1,735.116,70.987Z"
            transform="translate(-728.842 -56.615)"
          />
          <path
            d="M781.9,67.849a2.112,2.112,0,1,0,2.112,2.112A2.112,2.112,0,0,0,781.9,67.849Zm0,3.138a1.026,1.026,0,1,1,1.026-1.026A1.027,1.027,0,0,1,781.9,70.987Z"
            transform="translate(-763.675 -56.615)"
          />
          <rect
            width="7.982"
            height="1.086"
            transform="translate(1.831 2.261)"
          />
          <rect width="7.982" height="1.086" transform="translate(0 4.453)" />
          <rect
            width="7.982"
            height="1.086"
            transform="translate(1.653 6.646)"
          />
        </svg>
        Môžete mať už zajtra. Doba dodania je približne 24 hod.
      </p>
    </div>
  );
};

const CompareButton = ({ productId }) => {
  const { isLoading, itemAdd, itemRemove, itemExists } = useCompare();

  const text = isLoading
    ? "Spracovávam"
    : !itemExists(productId)
    ? "Porovnať"
    : "Odstrániť z porovnania";

  return (
    <a
      onClick={(e) =>
        !isLoading
          ? itemExists(productId)
            ? itemRemove(productId)
            : itemAdd(productId)
          : null
      }
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="26.347"
        height="22.344"
        viewBox="0 0 35.866 25.747"
      >
        <path
          d="M248.228,330.781H241a3.383,3.383,0,0,0-6.462,0h-7.229s-7.473,11.767-7.473,15.894a7.473,7.473,0,1,0,14.946,0c0-3.067-4.125-10.349-6.245-13.89h6a3.383,3.383,0,0,0,6.462,0h6c-2.12,3.541-6.245,10.823-6.245,13.89a7.473,7.473,0,1,0,14.946,0C255.7,342.548,248.228,330.781,248.228,330.781Zm-20.92,3.847c2.294,3.9,4.7,8.558,5.317,11.045H221.991C222.6,343.187,225.015,338.527,227.308,334.628Zm0,17.516a5.474,5.474,0,0,1-5.372-4.467h10.745A5.475,5.475,0,0,1,227.308,352.144Zm10.46-18.982a1.378,1.378,0,1,1,1.379-1.378A1.38,1.38,0,0,1,237.768,333.162ZM253.7,346.675c0,.022,0,.042,0,.064v-.105C253.694,346.646,253.7,346.663,253.7,346.675Zm-.151-1H242.911c.612-2.486,3.024-7.146,5.317-11.045C250.521,338.527,252.932,343.187,253.545,345.673Zm-5.317,6.471a5.475,5.475,0,0,1-5.373-4.467H253.6A5.475,5.475,0,0,1,248.228,352.144Z"
          transform="translate(-219.835 -328.401)"
        />
      </svg>
      {text}
    </a>
  );
};

const WishlistButton = ({ productId }) => {
  const { isLoading, itemAdd, itemRemove, itemExists } = useWishlist();

  const text = isLoading
    ? "Spracovávam"
    : !itemExists(productId)
    ? "Pridať medzi obľúbené"
    : "Odstrániť z obľúbených";

  return (
    <a
      onClick={(e) =>
        !isLoading
          ? itemExists(productId)
            ? itemRemove(productId)
            : itemAdd(productId)
          : null
      }
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="26.347"
        height="22.344"
        viewBox="0 0 26.347 22.344"
      >
        <path
          d="M373.027,203.507l-10.814-9.923a7.027,7.027,0,0,1,0-10.508,7.6,7.6,0,0,1,10.084,0l.73.669.729-.669a7.6,7.6,0,0,1,10.084,0h0a7.029,7.029,0,0,1,0,10.507Zm-5.772-19.862a4.957,4.957,0,0,0-3.354,1.272,4.531,4.531,0,0,0,0,6.827l9.125,8.372,9.124-8.373a4.532,4.532,0,0,0,0-6.827h0a5.056,5.056,0,0,0-6.706,0l-2.418,2.218-2.419-2.218A4.954,4.954,0,0,0,367.255,183.645Z"
          transform="translate(-359.852 -181.163)"
        />
      </svg>
      {text}
    </a>
  );
};

const Product = (props) => {
  const [activeTab, setActiveTab] = useState("description");
  const router = useRouter();
  const { user } = useUser();
  const { data: product, error } = useSWR(
    `/api/lib/v1/products/${get(router, "query.id")}?hash=${get(
      router,
      "query.hash"
    )}`,
    (url) =>
      fetch(url, {
        headers: reqGetHeadersFront(null, { forwardUrl: router.asPath }),
      }).then((r) => r.json()),
    {
      initialData: props.product,
      revalidateOnMount: true,
    }
  );

  React.useEffect(() => {
    TagManager.push(EnhancedEcommerce.detail(product));
    TagManager.push(Remarketing.viewItem(product));
    FBPixel.track("ViewContent", FBPixel.productDetail(product));
    FBCAPI.send("ViewContent", FBCAPI.productDetail(product));
    LeadhubViewContent(product?.id);
  }, []);

  const groupedParameters = groupBy(
    get(product, "detailedParameters", []),
    (parameter) => parameter.group
  );

  const crosssaleProducts = get(product, "crosssaleProducts", []);
  const defaultCrosssaleProducts = filter(
    crosssaleProducts,
    (crossSale) => crossSale?.hash !== ALTERNATIVE_PRODUCTS_CROSSSALE_HASH
  );
  const alternativeCrosssaleProducts = filter(crosssaleProducts, {
    hash: ALTERNATIVE_PRODUCTS_CROSSSALE_HASH,
  });

  return (
    <main className={styles.detailPage}>
      <MetaData
        title={get(product, "name")}
        {...get(product, "meta", {})}
        photo={get(product, "photo", {})}
      />

      <div className="scrollTop" id="scrollTop">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13.939"
          height="9.547"
          viewBox="0 0 13.939 9.547"
        >
          <path
            d="M8.577,9.353h0a.661.661,0,0,0,.936,0L13.745,5.48a.662.662,0,0,0,0-.936L9.513.194A.662.662,0,1,0,8.6,1.15l3.089,3.192H.662A.662.662,0,0,0,0,5v.029a.661.661,0,0,0,.662.661H11.674L8.6,8.4a.661.661,0,0,0-.021.955"
            transform="translate(0 0)"
          />
        </svg>
      </div>

      <div className="custom-container" id="container">
        <Breadcrumbs
          data={get(product, "breadcrumbs", [])}
          current={get(product, "name")}
        />

        <div className={styles.productWrapper}>
          <div className="row">
            <div className="col-xl-6 col-lg-6">
              <div className={styles.product}>
                <div className={styles.heading}>
                  <h1>{product?.name}</h1>
                  <div className={styles.badges}>
                    {product?.new ? (
                      <span className={styles.new}>Výhodné</span>
                    ) : (
                      ""
                    )}
                    {product?.favourite ? (
                      <span className={styles.favourite}>Obľúbené</span>
                    ) : (
                      ""
                    )}
                  </div>
                </div>

                <Gallery
                  titleImage={get(product, "photo", null)}
                  images={get(product, "gallery", [])}
                  showThumbnails
                  options={{
                    imageMainProps: {
                      size: "525x470",
                    },
                    imageThumbnailProps: {
                      size: "100x100",
                    },
                  }}
                />

                <div
                  className={[styles.priceBox, styles.priceBoxMobile].join(" ")}
                >
                  <p className={styles.stock}>
                    <strong>{product?.storeStatus?.message}</strong>
                  </p>
                  <img
                    src="https://instance5.admin.grandus.sk/files/attachment/2020-11/6466/8e0bfc/big-on-character.png"
                    alt=""
                  />
                  {/* {isEmpty(user) ? (
                    <RegisterUserPrice
                      productPrice={product?.finalPriceData?.price}
                    />
                  ) : null} */}
                  <PriceBox product={product} />
                </div>

                <AddToCartButton
                  stores={get(product, "store", [])}
                  productId={get(product, "id")}
                  options={{
                    class: "addCartMobile",
                    eecProduct: {
                      name: get(product, "name"),
                      id: get(product, "id"),
                      finalPriceData: get(product, "finalPriceData"),
                      brand: get(product, "brand"),
                      categories: get(product, "categories"),
                    },
                  }}
                />

                {/* <DeliveryInfo
                  options={{
                    class: "deliveryInfoMobile",
                  }}
                /> */}

                <div
                  className={[styles.actions, styles.actionsMobile].join(" ")}
                >
                  <div className={styles.box}>
                    <p>
                      <WishlistButton productId={product.id} />
                    </p>
                    <p>
                      <CompareButton productId={product.id} />
                    </p>
                  </div>
                  <div className={styles.box}>
                    <p>Nákup na splátky</p>
                    <QuatroButton
                      price={
                        product?.specialPrice
                          ? get(product, "specialPrice.priceData.price")
                          : get(product, "finalPriceData.price")
                      }
                    />
                  </div>
                </div>

                <div className={styles.productContact}>
                  <div className={styles.box}>
                    <p>Objednať telefonicky</p>
                    <a href="tel:" className={styles.withBg}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16.153"
                        height="16.142"
                        viewBox="0 0 16.153 16.142"
                      >
                        <path
                          d="M283.772,359.138a.781.781,0,0,1-.24.684l-2.274,2.257a1.279,1.279,0,0,1-.4.291,1.66,1.66,0,0,1-.487.154l-.1.008c-.057.006-.131.009-.222.009a7.883,7.883,0,0,1-1.052-.111,7.975,7.975,0,0,1-1.778-.547,14.921,14.921,0,0,1-2.386-1.308,17.794,17.794,0,0,1-2.839-2.394,18.769,18.769,0,0,1-1.984-2.257,16.153,16.153,0,0,1-1.265-1.992,10.738,10.738,0,0,1-.718-1.659,8.778,8.778,0,0,1-.325-1.282,4.213,4.213,0,0,1-.069-.847c.011-.2.018-.319.018-.342a1.647,1.647,0,0,1,.154-.487,1.287,1.287,0,0,1,.291-.4l2.274-2.274a.753.753,0,0,1,.547-.24.635.635,0,0,1,.393.128,1.181,1.181,0,0,1,.291.316l1.83,3.471a.831.831,0,0,1,.085.6,1.063,1.063,0,0,1-.291.547l-.838.838a.292.292,0,0,0-.06.111.418.418,0,0,0-.026.128,3.1,3.1,0,0,0,.308.821,7.524,7.524,0,0,0,.633,1,11.059,11.059,0,0,0,1.214,1.359,11.22,11.22,0,0,0,1.368,1.223,7.9,7.9,0,0,0,1,.641,2.63,2.63,0,0,0,.616.248l.213.043a.4.4,0,0,0,.111-.026.286.286,0,0,0,.111-.06l.975-.992a1.048,1.048,0,0,1,.718-.274.894.894,0,0,1,.462.1h.017l3.3,1.949A.862.862,0,0,1,283.772,359.138Z"
                          transform="translate(-267.626 -346.399)"
                        />
                      </svg>
                      ( +421) 048/ 399 60 30
                    </a>
                  </div>
                  <div className={styles.box}>
                    <p>Osobný odber na predajni</p>
                    <span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16.153"
                        height="16.142"
                        viewBox="0 0 19.38 30"
                      >
                        <path
                          d="M108.616,248.191a9.7,9.7,0,0,0-9.69,9.69c0,4.91,7.83,17.381,8.723,18.787l.967,1.523.967-1.523c.892-1.406,8.723-13.877,8.723-18.787A9.7,9.7,0,0,0,108.616,248.191Zm0,25.689c-2.706-4.45-7.4-12.827-7.4-16a7.4,7.4,0,1,1,14.8,0C116.015,261.051,111.322,269.429,108.616,273.88Z"
                          transform="translate(-98.926 -248.191)"
                        />
                        <circle
                          cx="3.235"
                          cy="3.235"
                          r="3.235"
                          transform="translate(6.685 5.741)"
                        />
                      </svg>
                      Banská Bystrica
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={[
                styles.colWithBg,
                "col-xl-5",
                "col-lg-6",
                "offset-xl-1",
              ].join(" ")}
            >
              <div className={[styles.productInfo, styles.product].join(" ")}>
                <div className={styles.brand}>
                  {product?.brand?.photo ? (
                    <Image
                      photo={product.brand.photo}
                      size={"85x28"}
                      type={"jpg"}
                    />
                  ) : (
                    <strong>{product?.brand?.name}</strong>
                  )}
                </div>
                <div className={styles.stockInfo}>
                  <p>{product?.storeStatus?.message}</p>
                </div>
                <div className={styles.productTitle}>
                  {/* <h4>{product.name}</h4> */}
                  <p>
                    EAN: {product.ean} / Kód: {product.partNo} / ID:{" "}
                    {product.id}
                  </p>
                </div>
                <div className={styles.productDesc}>
                  <div
                    className={get(styles, "short-description")}
                    dangerouslySetInnerHTML={{
                      __html: get(
                        product,
                        "shortProductDescription.description"
                      ),
                    }}
                  />

                  <div>
                    {round(product?.warrantyDuration / 365) ? (
                      <p>
                        <span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12.884"
                            height="10.962"
                            viewBox="0 0 12.884 10.962"
                          >
                            <path
                              d="M12.873.725a.857.857,0,0,0-1.54-.372L5.1,8.906,1.363,6.183a.856.856,0,0,0-1.2.188.857.857,0,0,0,.188,1.2L4.78,10.8a.858.858,0,0,0,1.2-.188l6.74-9.247a.85.85,0,0,0,.154-.638"
                              transform="translate(0 0)"
                            />
                          </svg>
                        </span>
                        <strong>Záruka:</strong>{" "}
                        {round(product?.warrantyDuration / 365) == 1
                          ? "1 rok"
                          : `${round(product?.warrantyDuration / 365)} roky`}
                      </p>
                    ) : (
                      ""
                    )}

                    {get(product, "condition") ? (
                      <p>
                        <span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12.884"
                            height="10.962"
                            viewBox="0 0 12.884 10.962"
                          >
                            <path
                              d="M12.873.725a.857.857,0,0,0-1.54-.372L5.1,8.906,1.363,6.183a.856.856,0,0,0-1.2.188.857.857,0,0,0,.188,1.2L4.78,10.8a.858.858,0,0,0,1.2-.188l6.74-9.247a.85.85,0,0,0,.154-.638"
                              transform="translate(0 0)"
                            />
                          </svg>
                        </span>
                        <strong>Stav:</strong> {get(product, "condition")}
                      </p>
                    ) : (
                      ""
                    )}
                  </div>
                </div>

                <div
                  className={[styles.priceBox, styles.priceBoxDesktop].join(
                    " "
                  )}
                >
                  <img
                    src="https://instance5.admin.grandus.sk/files/attachment/2020-11/6463/8bfe9f/detail-logo.png"
                    alt=""
                  />
                  {/* {isEmpty(user) ? (
                    <RegisterUserPrice
                      productPrice={product?.finalPriceData?.price}
                    />
                  ) : null} */}
                  <PriceBox product={product} />
                </div>

                <AddToCartButton
                  stores={get(product, "store", [])}
                  productId={get(product, "id")}
                  options={{
                    class: "addCartDesktop",
                    eecProduct: {
                      name: get(product, "name"),
                      id: get(product, "id"),
                      finalPriceData: get(product, "finalPriceData"),
                      brand: get(product, "brand"),
                      categories: get(product, "categories"),
                    },
                  }}
                />

                {/* <DeliveryInfo
                  options={{
                    class: "deliveryInfoDesktop",
                  }}
                /> */}

                <div
                  className={[styles.actions, styles.actionsDesktop].join(" ")}
                >
                  <div className={styles.box}>
                    <p>
                      <WishlistButton productId={product.id} />
                    </p>
                    <p>
                      <CompareButton productId={product.id} />
                    </p>
                  </div>
                  <div className={styles.box}>
                    <p>Nákup na splátky</p>
                    <QuatroButton
                      price={
                        product?.specialPrice
                          ? get(product, "specialPrice.priceData.price")
                          : get(product, "finalPriceData.price")
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CrossSale data={defaultCrosssaleProducts} />

      <section className={styles.productInformations}>
        <div className="custom-container">
          <div className={[styles.anchors, "js-anchors"].join(" ")}>
            <a
              className={activeTab == "description" ? styles.active : ""}
              onClick={(e) => {
                setActiveTab("description");
              }}
            >
              Popis
            </a>
            <a
              className={activeTab == "parameters" ? styles.active : ""}
              onClick={(e) => {
                setActiveTab("parameters");
              }}
            >
              Parametre
            </a>
            {!isEmpty(product?.additionalInfos)
              ? map(product?.additionalInfos, (additionalInfo) => (
                  <a
                    className={
                      activeTab == `additional-info-${additionalInfo?.id}`
                        ? styles.active
                        : ""
                    }
                    onClick={(e) => {
                      setActiveTab(`additional-info-${additionalInfo?.id}`);
                    }}
                  >
                    {additionalInfo?.title}
                  </a>
                ))
              : null}
            {!isEmpty(alternativeCrosssaleProducts) ? (
              <a
                className={
                  activeTab == "alternativeProducts" ? styles.active : ""
                }
                onClick={(e) => {
                  setActiveTab("alternativeProducts");
                }}
              >
                Alternatívne produkty
              </a>
            ) : null}
          </div>

          {activeTab == "description" ? (
            <div
              className={styles.productMainDescription}
              dangerouslySetInnerHTML={{
                __html: get(product, "productDescription.description"),
              }}
            />
          ) : (
            ""
          )}

          {activeTab == "parameters" ? (
            <div className={styles.parameters}>
              {map(groupedParameters, (group, label) => {
                return (
                  <div
                    key={`detailed-parameter-wrapper-${
                      group.length
                    }-${encodeURIComponent(label)}`}
                  >
                    {label != "null" ? <h3>{label}</h3> : ""}
                    <table>
                      <tbody>
                        {map(
                          groupBy(group, (groupItem) => groupItem.parameterId),
                          (item, index) => {
                            let groupped = [];
                            if (
                              get(item, [0, "hash"]) === COUNTDOWN_PARAM_HASH
                            ) {
                              return null;
                            }

                            map(item, (row) =>
                              groupped.push(get(row, "value"))
                            );

                            return (
                              <tr
                                key={`detailed-parameter-${index}-${groupped.length}`}
                              >
                                <td>{get(item, "[0].name")}</td>
                                <td>{groupped.join(", ")}</td>
                              </tr>
                            );
                          }
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          ) : (
            ""
          )}

          {!isEmpty(product?.additionalInfos)
            ? map(product?.additionalInfos, (additionalInfo) =>
                activeTab == `additional-info-${additionalInfo.id}` ? (
                  <div
                    className={styles.productMainDescription}
                    dangerouslySetInnerHTML={{
                      __html: get(additionalInfo, "text"),
                    }}
                  />
                ) : (
                  ""
                )
              )
            : null}

          {!isEmpty(alternativeCrosssaleProducts) &&
          activeTab == "alternativeProducts" ? (
            <>
              {map(alternativeCrosssaleProducts, (crossSale) => (
                <ProductsCarousel products={crossSale?.products} />
              ))}
            </>
          ) : null}
        </div>
      </section>
    </main>
  );
};

export async function getServerSideProps(context) {
  return productPage.serverSideProps(context);
}

export default Product;
